import { observer } from 'mobx-react-lite'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { type JobProgressionViewModel } from '@/features/jobs/details/components/JobProgressionCard/JobProgressionViewModel'
import { DraftInReviewProgressionActions } from '@/features/jobs/details/components/JobProgressionCard/DraftInReviewProgressionActions'
import { NoProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/NoProgressionActions/NoProgressionActionsContainer'
import { JobStartedProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/JobStartedProgressionActions/JobStartedProgressionActionsContainer'
import { OpenItemsProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/OpenItemsProgressionActions/OpenItemsProgressionActionsContainer'
import { DraftRejectedProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/DraftRejectedProgressionActions/DraftRejectedProgressionActionsContainer'
import { DraftApprovedProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/DraftApprovedProgressionActions/DraftApprovedProgressionActionsContainer'
import { AuthorizationSignedProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/AuthorizationSignedProgressionActions/AuthorizationSignedProgressionActionsContainer'
import { AuthorizationRequestedProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/AuthorizationRequestedProgressionActions/AuthorizationRequestedProgressionActionsContainer'
import { DeliverableCompletedProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/DeliverableCompletedProgressionActions/DeliverableCompletedProgressionActionsContainer'
import { CompleteJobProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/CompleteJobProgressionActions/CompleteJobProgressionActionsContainer'
import { ReturnFiledProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/ReturnFiledProgressionActions/ReturnFiledProgressionActionsContainer'
import { JobExtensionProgressionNotStartedActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/JobExtensionProgressionNotStartedActions/JobExtensionProgressionNotStartedActionsContainer'
import { JobExtensionInProgressProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/JobExtensionInProgressProgressionActions/JobExtensionInProgressProgressionActionsContainer'
import { JobExtensionConfirmedProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/JobExtensionConfirmedProgressionActions/JobExtensionConfirmedProgressionActionsContainer'
import { JobExtensionCompletedProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/JobExtensionCompletedProgressionActions/JobExtensionCompletedProgressionActionsContainer'
import { WaitingOnClientProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/WaitingOnClientProgressionActions/WaitingOnClientProgressionActionsContainer'
import { WaitingOnClientProgressionProActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/WaitingOnClientProgressionProActions/WaitingOnClientProgressionProActionsContainer'
import { DraftInReviewProgressionProActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/DraftInReviewProgressionProActions/DraftInReviewProgressionProActionsContainer'
import { WaitingOnAdminProgressionActionsContainer } from '@/features/jobs/details/components/JobProgressionCard/WaitingOnAdminProgressionActions/WaitingOnAdminProgressionActionsContainer'

export interface SupportingClientProgressionActionsContainerProps {
  vm: JobDetailsViewModel
}

export const SupportingClientProgressionActionsContainer = observer(
  function SupportingClientProgressionActionsContainer({
    vm,
  }: SupportingClientProgressionActionsContainerProps) {
    switch (vm.jobProgressionVm.currentStepStatus) {
      case 'DraftInReview':
        return (
          <DraftInReviewProgressionActions
            onShowApproveDraftDialog={
              vm.jobProgressionVm.approveDraftInReviewDialogVm.modal.show
            }
            onShowRejectDraftDialog={
              vm.jobProgressionVm.rejectDraftInReviewDialogVm.modal.show
            }
          />
        )
      default:
        return <></>
    }
  },
)

export interface ClientProgressionActionsContainerProps {
  vm: JobDetailsViewModel
}

export const ClientProgressionActionsContainer = observer(
  function ClientProgressionActionsContainer({
    vm,
  }: ClientProgressionActionsContainerProps) {
    const jobProgressionVm = vm.jobProgressionVm

    switch (jobProgressionVm.currentStepStatus) {
      case 'DraftInReview':
        return (
          <DraftInReviewProgressionActions
            onShowApproveDraftDialog={
              vm.jobProgressionVm.approveDraftInReviewDialogVm.modal.show
            }
            onShowRejectDraftDialog={
              vm.jobProgressionVm.rejectDraftInReviewDialogVm.modal.show
            }
          />
        )
      case 'ProgressionWaitingOnClient':
        return (
          <WaitingOnClientProgressionActionsContainer vm={jobProgressionVm} />
        )
      default:
        return <></>
    }
  },
)

export interface AdminProgressionActionsContainerProps {
  vm: JobDetailsViewModel
}

export const AdminProgressionActionsContainer = observer(
  function AdminProgressionActionsContainer({
    vm,
  }: AdminProgressionActionsContainerProps) {
    const jobProgressionVm = vm.jobProgressionVm

    switch (jobProgressionVm.currentStepStatus) {
      case 'ProgressionWaitingOnClient':
        return (
          <WaitingOnAdminProgressionActionsContainer vm={jobProgressionVm} />
        )
      case 'DraftInReview':
        return (
          vm.canReviewDraftAsAdmin && (
            <DraftInReviewProgressionActions
              onShowApproveDraftDialog={
                vm.jobProgressionVm.approveDraftInReviewDialogVm.modal.show
              }
              onShowRejectDraftDialog={
                vm.jobProgressionVm.rejectDraftInReviewDialogVm.modal.show
              }
            />
          )
        )
      default:
        return <></>
    }
  },
)

export interface ProviderProgressionActionsContainerProps {
  vm: JobProgressionViewModel
  jobVm: JobDetailsViewModel
  canCompleteJob: boolean
}

export const ProviderProgressionActionsContainer = observer(
  function ProviderProgressionActionsContainer({
    vm,
    jobVm,
    canCompleteJob,
  }: ProviderProgressionActionsContainerProps) {
    switch (vm.currentStepStatus) {
      case 'OpenItems':
        return <OpenItemsProgressionActionsContainer vm={vm} />
      case 'JobStarted':
        return <JobStartedProgressionActionsContainer vm={vm} />
      case 'DraftInReview':
        return <DraftInReviewProgressionProActionsContainer vm={vm} />
      case 'DraftRejected':
        return <DraftRejectedProgressionActionsContainer vm={vm} />
      case 'DraftApproved':
        return (
          <DraftApprovedProgressionActionsContainer
            vm={vm}
            jobVm={jobVm}
            canCompleteJob={canCompleteJob}
          />
        )
      case 'AuthorizationRequested':
        return <AuthorizationRequestedProgressionActionsContainer vm={vm} />
      case 'AuthorizationSigned':
        return <AuthorizationSignedProgressionActionsContainer vm={vm} />
      case 'ReturnFiled':
        return (
          <ReturnFiledProgressionActionsContainer
            vm={vm}
            jobVm={jobVm}
            canCompleteJob={canCompleteJob}
          />
        )
      case 'DeliverableCompleted':
        return (
          <DeliverableCompletedProgressionActionsContainer
            vm={vm}
            canCompleteJob={canCompleteJob}
          />
        )
      case 'NotStarted':
        return <NoProgressionActionsContainer vm={vm} />

      case 'ExtensionNotStarted':
        return <JobExtensionProgressionNotStartedActionsContainer vm={vm} />

      case 'ExtensionInProgress':
        return <JobExtensionInProgressProgressionActionsContainer vm={vm} />

      case 'ExtensionConfirmed':
        return <JobExtensionConfirmedProgressionActionsContainer vm={vm} />

      case 'ExtensionAccepted':
      case 'ExtensionRejected':
      case 'ReadyToFile':
        return (
          <JobExtensionCompletedProgressionActionsContainer
            vm={vm}
            canCompleteJob={canCompleteJob}
          />
        )

      case 'ProgressionWaitingOnClient':
        return <WaitingOnClientProgressionProActionsContainer vm={vm} />

      case 'PreparationCompleted':
        return <CompleteJobProgressionActionsContainer vm={vm} />
      case 'ReviewCompleted':
        return <CompleteJobProgressionActionsContainer vm={vm} />

      default:
        return <></>
    }
  },
)
